/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/ActionCreatePage';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCreate';
import { analyzeActionCreate } from './ActionCreateAnalyze';

const ActionCreateManager: ActionManagerFn<'ActionCreate'> = (
  platform: Platform,
): ActionManager<'ActionCreate'> => {
  return {
    ...baseActionManager(platform, 'ActionCreate'),
    isChildAllowed: (page, item, childType) =>
      ['ActionSet'].includes(childType as ItemType),
    defaultValue,
    defaultChildren: ['ActionSet'],
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: [
          'CloudDataStore',
          'DataStore',
          'LocalDataStore',
          'TransientDataStore',
          'ReadOnlyDataStore',
        ],
      },
      addOnTop: {
        label: 'Add On Top',
        type: 'boolean',
        helpText:
          'Turn this off if you want the newly added row to be inserted to the bottom of all the existing records.',
      },
      markAsDirty: {
        label: 'Mark As Dirty',
        type: 'boolean',
        helpText:
          'If the newly added record is marked as dirty, then it will be posted to the db even if the record is not changed by the user. By default, all the newly added rows will not be posted to the db. Only the records that are changed (dirty) by the user will get posted.',
      },
    },
    name: 'Create Record',
    getDisplayText: (item) => item.props.alias,
    getDataSourceAliasProperty: (aliasType: AliasType) =>
      aliasType === 'STORE'
        ? { aliasProp: 'alias', embeddedPageIdProp: 'embeddedPageId' } // TODO: Add embeddedPageId prop
        : undefined,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'plus' },
    analyze: analyzeActionCreate,
    description:
      'Use this action to create/insert an empty record into a Data Store. Use one or more Set child actions to populate it with default values for one or more attributes.',
    helpRenders: [
      {
        title: 'Action Create Record',
        description:
          "Try changing the props and press the below 'Inovke Action' button to see it in action! Press the 'Reset' button to remove the newly added rows from the store.",
        page: SamplePage,
        height: 300,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: ['addOnTop'],
        item: {
          children: [],
          itemId: 'actionCreate',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
          },
          type: 'ActionCreate',
        },
      },
    ],
  };
};

export default ActionCreateManager;
